import { useState, useEffect, useCallback } from 'react';
import { Download, X, Share } from 'lucide-react';

const DISMISS_KEY = 'splashable_install_dismissed';
const DISMISS_DAYS = 14; // Re-show after 14 days

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://')
  );
}

function isIOS() {
  return /iP(hone|ad|od)/.test(navigator.userAgent) && !window.MSStream;
}

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Date.now() - ts < DISMISS_DAYS * 86400000) return true;
    localStorage.removeItem(DISMISS_KEY);
    return false;
  } catch { return false; }
}

export function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [installing, setInstalling] = useState(false);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setShowBanner(false);
  }, []);

  const requestPushPermission = useCallback(async () => {
    if (!('Notification' in window) || Notification.permission !== 'default') return;
    try {
      const result = await Notification.requestPermission();
      if (result === 'granted') {
        // Register for push if permission granted
        const reg = await navigator.serviceWorker?.ready;
        if (reg) {
          const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
          if (vapidKey) {
            await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: vapidKey,
            });
          }
        }
      }
    } catch (e) { /* user denied or error */ }
  }, []);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    // iOS — always show instructions banner
    if (isIOS()) {
      setShowBanner(true);
      return;
    }

    // Android/Chrome — listen for the native install event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Fallback: if no prompt event fires after 3s on a mobile browser, show generic banner
    const fallback = setTimeout(() => {
      if (!isStandalone() && /Mobi|Android/i.test(navigator.userAgent)) {
        setShowBanner(true);
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(fallback);
    };
  }, []);

  // Hide once app is installed
  useEffect(() => {
    const mql = window.matchMedia('(display-mode: standalone)');
    const onChange = () => { if (mql.matches) setShowBanner(false); };
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
      // After install, request push notification permission
      requestPushPermission();
    }
    setDeferredPrompt(null);
    setInstalling(false);
  };

  // Also request push permission when app is opened in standalone mode for the first time
  useEffect(() => {
    if (isStandalone() && 'Notification' in window && Notification.permission === 'default') {
      // Small delay to let the app settle
      const timer = setTimeout(() => requestPushPermission(), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  if (!showBanner) return null;

  const ios = isIOS();

  return (
    <div
      className="mx-4 sm:mx-6 lg:mx-8 mb-4 bg-gradient-to-r from-[#1E1B4B] to-[#312E81] text-white rounded-2xl p-4 flex items-start gap-3 shadow-lg animate-fadeIn"
      data-testid="install-banner"
    >
      <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0 mt-0.5">
        <Download className="w-5 h-5 text-[#C4F017]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm" style={{ fontFamily: 'Nunito' }}>
          Install SplashAble
        </p>
        {ios ? (
          <p className="text-xs text-white/70 mt-1 leading-relaxed">
            Tap the <Share className="w-3.5 h-3.5 inline -mt-0.5 text-[#22D3EE]" /> <strong className="text-white">Share</strong> button in your browser, then tap <strong className="text-white">"Add to Home Screen"</strong> for the best experience.
          </p>
        ) : (
          <p className="text-xs text-white/70 mt-1 leading-relaxed">
            Add SplashAble to your home screen for quick access and a full-screen experience.
          </p>
        )}
        {!ios && deferredPrompt && (
          <button
            onClick={handleInstall}
            disabled={installing}
            className="mt-2.5 px-4 py-2 rounded-full bg-[#C4F017] text-[#1E1B4B] text-xs font-bold hover:bg-[#d4f547] transition-colors"
            data-testid="install-btn"
          >
            {installing ? 'Installing...' : 'Install Now'}
          </button>
        )}
      </div>
      <button
        onClick={dismiss}
        className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0 hover:bg-white/20 transition-colors"
        data-testid="dismiss-install-banner"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-white/60" />
      </button>
    </div>
  );
}
